import React, { Suspense, lazy, ReactNode } from 'react';
import { getAgainst } from 'presentation/utils/colorway';
import { Layout } from 'domain/constants/layout';
/*
  These shortcodes should not be lazy loaded in as they are already in the
  main bundle and it causes a unnecessary shimmer which makes the page feel
  less responsive
*/
import { Colorway as Against } from '@uds/theme-utils';
import { IconName } from '@uds/glyphs';

import Alt from './Alt';
import Bold from './Bold';
import Italic from './Italic';
import Link from './Link';
import Text from './Text';
import Tooltip from './Tooltip';
import PendingShortcode from './PendingShortcode';

import { ShortcodeProps } from '.';

const Glyph = lazy(
  () =>
    import(
      /* webpackChunkName: "shortcode-glyph" */
      './Glyph'
    ),
);

const Sup = lazy(
  () =>
    import(
      /* webpackChunkName: "shortcode-sup" */
      './Sup'
    ),
);

const List = lazy(
  () =>
    import(
      /* webpackChunkName: "shortcode-list" */
      './List'
    ),
);

const OrderedList = lazy(
  () =>
    import(
      /* webpackChunkName: "shortcode-ordered-list" */
      './OrderedList'
    ),
);

const ListItem = lazy(
  () =>
    import(
      /* webpackChunkName: "shortcode-list-item" */
      './ListItem'
    ),
);

const TickListItem = lazy(
  () =>
    import(
      /* webpackChunkName: "shortcode-tick-list-item" */
      './TickListItem'
    ),
);

const ProductPrice = lazy(
  () =>
    import(
      /* webpackChunkName: "shortcode-product-price" */
      './ProductPrice'
    ),
);

const PhoneNumber = lazy(
  () =>
    import(
      /* webpackChunkName: "shortcode-phone-number" */
      './PhoneNumber'
    ),
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const i: number;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const element: ShortcodeProps;

export interface ShortcodeAttributesProps {
  glyph: string;
  use?: number;
  number?: number;
  RecursiveShortcode: (props: RecursiveShortcodeProps) => JSX.Element;
  analyticskey?: string;
  goto?: string;
  label?: ReactNode;
  icon: IconName;
  prices?: Array<{
    gross: number;
    net: number;
    raw: number;
    tax: number;
  }>;
  inctax?: string;
  style?: string;
  rel?: string;
  newTab?: string;
}
export interface RecursiveShortcodeProps {
  elements?: Array<ShortcodeProps>;
  RecursiveShortcode?: (props: RecursiveShortcodeProps) => JSX.Element;
  type?: string;
  name?: string;
  layout?: Layout;
  against?: Against;
  attributes?: ShortcodeAttributesProps | undefined;
  nl2br?: boolean;
  rest?: {
    [key: string]: any;
  };
  [key: string]: any;
}

const RecursiveShortcode = ({ elements = [], ...rest }: RecursiveShortcodeProps) => (
  <>
    {elements.map((element, index) => (
      // Temporarily allow index as key waiting for BE to add ids
      // eslint-disable-next-line react/no-array-index-key
      <Shortcode key={`stringed-shortcode-${JSON.stringify(element)}-${index}`} code={element} {...rest} />
    ))}
  </>
);

export interface ShortcodeOuterProps {
  against?: Against;
  code: RecursiveShortcodeProps;
  type?: string;
  name?: string;
  values?: Record<string, string>;
}

const Shortcode = ({ code, ...rest }: RecursiveShortcodeProps) => {
  let props = {
    ...rest,
  } as RecursiveShortcodeProps;
  if (typeof code === 'string' || code instanceof String) {
    props.code = code;
  } else {
    props = {
      ...props,
      ...code,
    };
  }

  props.against = getAgainst(props.against);
  props.RecursiveShortcode = RecursiveShortcode;
  props.attributes = props?.attributes ?? ({} as ShortcodeAttributesProps);

  const { type, name, values } = props;
  if (typeof props.code === 'string') {
    return <Text text={code} values={values} />;
  }
  if (props.type === 'text') {
    return <Text {...props} values={values} />;
  }
  if (type === 'element') {
    switch (name) {
      case 'list':
        if (props?.attributes?.style === 'number') {
          return <OrderedList {...props} />;
        }
        return <List {...props} />;
      case 'li':
        if (props?.attributes?.style === 'glyph') {
          return <TickListItem {...props} glyph={props.attributes?.glyph ?? 'tick'} />;
        }
        return <ListItem {...props} />;
      case 'i':
        return <Italic {...props} />;
      case 'b':
        return <Bold {...props} />;
      case 'alt':
        return <Alt {...props} />;
      case 'sup':
        return <Sup {...props} />;
      case 'glyph':
        return <Glyph attributes={props.attributes} {...props} />;
      case 'tel':
        return <PhoneNumber {...props} />;
      case 'link':
        return <Link {...props} />;
      case 'productprice':
        return <ProductPrice {...props} />;
      case 'tooltip':
        return <Tooltip {...props} />;
      case 'root':
        return <RecursiveShortcode {...props} />;
      default:
        return null;
    }
  }
  return null;
};

const ShortcodeWrapper = (props: RecursiveShortcodeProps) => (
  <Suspense fallback={<PendingShortcode code={props.code} />}>
    <Shortcode {...props} />
  </Suspense>
);

ShortcodeWrapper.displayName = 'Shortcode';

export default ShortcodeWrapper;
